import { KeyboardEvent, RefObject, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

import { adminScreenTitle } from "@pages/AdminDashboard/adminNavigation";
import { globalSearchAddress } from "@pages/AdminDashboard/adminRoutes";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";
import { SearchInput } from "@pages/AdminDashboard/ui/SearchInput";

import { ShellIconButton } from "./ShellIconButton";

const { color, typeScale } = adminTokens;
const [, spaceSm] = adminTokens.spacing;
// The design's app bar: 52px tall, padded 6px at each side, the title in Medium 17;
// the search bar pads 12px at its left and 8px at its right, and stands the search above
// Cancel, one row each (SHELL-R30, SHELL-R31, SHELL-R42, PHONE-R39).
const appBarHeight = 52;
const appBarPaddingX = 6;
const searchBarPaddingLeft = 12;
const searchBarPaddingRight = 8;
const titleFontSize = "17px";
const cancelFontSize = "14px";
const cancelHeight = 44;
const menuIconSize = 22;
const searchIconSize = 20;

const appBarLayout = css`
  align-items: center;
  gap: 2px;
  height: ${appBarHeight}px;
  padding: 0 ${appBarPaddingX}px;
`;

const searchBarLayout = css`
  flex-direction: column;
  align-items: stretch;
  gap: ${spaceSm}px;
  padding: ${spaceSm}px ${searchBarPaddingRight}px ${spaceSm}px ${searchBarPaddingLeft}px;
`;

const Bar = styled.header<{ $isSearch: boolean }>`
  display: flex;
  flex: none;
  box-sizing: border-box;
  ${({ $isSearch }) => ($isSearch ? searchBarLayout : appBarLayout)}
  background-color: ${color.surface};
  border-bottom: 1px solid ${color.border};
  color: ${color.navy};
`;

const Title = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${titleFontSize};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SearchSlot = styled.div`
  flex: 1;
  min-width: 0;
`;

const CancelButton = styled.button`
  flex: none;
  height: ${cancelHeight}px;
  padding: 0 ${appBarPaddingX}px;
  border: 0;
  background: transparent;
  color: ${color.tealDark};
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${cancelFontSize};
  cursor: pointer;

  &:focus-visible {
    ${focusOutline}
  }
`;

interface AdminAppBarProps {
  isMenuOpen: boolean;
  isSearchOpen: boolean;
  menuButtonRef: RefObject<HTMLButtonElement>;
  searchButtonRef: RefObject<HTMLButtonElement>;
  onOpenMenu: () => void;
  onOpenSearch: () => void;
  onCloseSearch: () => void;
  onSearched: () => void;
}

// The phone's app bar: Menu, the open screen's title and Search (SHELL-R29…R32); while
// the admin searches, the search bar takes its place (SHELL-R42…R45).
export const AdminAppBar = ({
  isMenuOpen,
  isSearchOpen,
  menuButtonRef,
  searchButtonRef,
  onOpenMenu,
  onOpenSearch,
  onCloseSearch,
  onSearched,
}: AdminAppBarProps) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSearchOpen) searchRef.current?.focus();
  }, [isSearchOpen]);

  const search = (entry: string) => {
    const address = globalSearchAddress(entry);
    if (!address) return;

    history.push(address);
    onSearched();
  };

  const closeOnEscape = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") onCloseSearch();
  };

  if (isSearchOpen) {
    return (
      <Bar $isSearch data-testid="admin-app-bar">
        <SearchSlot>
          <SearchInput
            ref={searchRef}
            label="Search for a User or an Order"
            onSearch={search}
            onKeyDown={closeOnEscape}
          />
        </SearchSlot>
        <CancelButton type="button" onClick={onCloseSearch}>
          Cancel
        </CancelButton>
      </Bar>
    );
  }

  return (
    <Bar $isSearch={false} data-testid="admin-app-bar">
      <ShellIconButton
        ref={menuButtonRef}
        label="Menu"
        isExpanded={isMenuOpen}
        onClick={onOpenMenu}
      >
        <svg
          aria-hidden="true"
          width={menuIconSize}
          height={menuIconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        >
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </ShellIconButton>
      <Title>{adminScreenTitle(pathname)}</Title>
      <ShellIconButton ref={searchButtonRef} label="Search" onClick={onOpenSearch}>
        <svg
          aria-hidden="true"
          width={searchIconSize}
          height={searchIconSize}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      </ShellIconButton>
    </Bar>
  );
};
